import { useMemo, useRef } from 'react'

const PRESIGNED_URL_REFRESH_LEEWAY_MS = 90_000
const AWS_DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/

type PresignedUrlMetadata = {
    expiresAt: number
    identity: string
}

function parseAwsDate(value: string | null): number | undefined {
    const match = value?.match(AWS_DATE_PATTERN)

    if (!match) {
        return
    }

    const [, year, month, day, hour, minute, second] = match

    return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
}

function getPresignedUrlMetadata(rawUrl: string): PresignedUrlMetadata | undefined {
    try {
        const url = new URL(rawUrl)
        const signedAt = parseAwsDate(url.searchParams.get('X-Amz-Date'))
        const expiresInSeconds = Number(url.searchParams.get('X-Amz-Expires'))

        if (!signedAt || !Number.isFinite(expiresInSeconds)) {
            return
        }

        const versionId = url.searchParams.get('versionId')

        return {
            expiresAt: signedAt + expiresInSeconds * 1000,
            identity: `${url.origin}${url.pathname}${versionId ? `?versionId=${versionId}` : ''}`,
        }
    } catch {
        return undefined
    }
}

function getStablePresignedUrl(currentUrl: string | undefined, nextUrl: string, refreshLeewayMs: number) {
    if (!currentUrl) {
        return nextUrl
    }

    const currentMetadata = getPresignedUrlMetadata(currentUrl)
    const nextMetadata = getPresignedUrlMetadata(nextUrl)

    if (!currentMetadata || currentMetadata.identity !== nextMetadata?.identity) {
        return nextUrl
    }

    return currentMetadata.expiresAt - Date.now() <= refreshLeewayMs ? nextUrl : currentUrl
}

export function useStablePresignedUrls(nextUrls: readonly string[], refreshLeewayMs = PRESIGNED_URL_REFRESH_LEEWAY_MS) {
    const stableUrlsRef = useRef<string[]>([])
    const nextUrlsKey = JSON.stringify(nextUrls)
    const memoizedNextUrls = useMemo(() => [...nextUrls], [nextUrlsKey])

    return useMemo(() => {
        const currentUrls = stableUrlsRef.current
        let hasChanges = currentUrls.length !== memoizedNextUrls.length
        const stableUrls = [...memoizedNextUrls]

        for (const [index, nextUrl] of memoizedNextUrls.entries()) {
            const stableUrl = getStablePresignedUrl(currentUrls[index], nextUrl, refreshLeewayMs)

            if (!hasChanges && stableUrl !== currentUrls[index]) {
                hasChanges = true
            }

            stableUrls[index] = stableUrl
        }

        if (hasChanges) {
            stableUrlsRef.current = stableUrls
        }

        return hasChanges ? stableUrls : currentUrls
    }, [memoizedNextUrls, refreshLeewayMs])
}
